import { PERMISSION } from '@/app/permissions';
import type { RouteObject } from 'react-router-dom';
import { RequirePermission } from '@/app/session';
import {
  InstitutionsAdminPage,
  UsersAdminPage,
  RolesAdminPage,
  AuditPage,
  ConfigPage,
  NewsAdminPage,
  PagesAdminPage,
  SiteHomeAdminPage,
  AdvisoryServicesReportPage,
  LibraryAdminPage,
  GeoPointAdminPage,
  ContactAdminPage,
  BackupAdminPage,
  WorkflowDesignerPage,
  WorkQueuePage,
  AdvisorValidationPage,
  FundingAdminPage,
  SupportAgencyAdminPage,
  AdminPage,
  ForumAdminPage,
} from '@/app/routes/adminPages';
// Also the organiser's own view in the cabinet, where its lazy page is declared.
import { EventEvaluationAnalysisPage } from '@/app/routes/cabinetPages';
import { INSTITUTION_PERMISSIONS } from '@/features/admin/institutions/institutionTypes';
import { EVALUATION_SCOPE } from '@/features/eventEvaluations/evaluationScope';
import { publicLinkPaths } from '@/features/siteHome/siteHomeEdit';
import { publicRoutes } from '@/app/routes/publicRoutes';

/**
 * The administration screens, inside AppShell — the sidebar's "Administrare" section (ADMIN_NAV in
 * shells/cabinet/cabinetNav.ts). Every one is behind the permission its backend endpoints check.
 */
export const adminRoutes: RouteObject[] = [
  {
    path: '/app/admin/institutii',
    element: (
      <RequirePermission permission={INSTITUTION_PERMISSIONS}>
        <InstitutionsAdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/utilizatori',
    element: (
      <RequirePermission permission={PERMISSION.usersAdmin}>
        <UsersAdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/roluri',
    element: (
      <RequirePermission permission={PERMISSION.usersAdmin}>
        <RolesAdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/audit',
    element: (
      <RequirePermission permission={PERMISSION.auditRead}>
        <AuditPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/configurare',
    element: (
      <RequirePermission permission={PERMISSION.nomenclatureAdmin}>
        <ConfigPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/stiri',
    element: (
      <RequirePermission permission={PERMISSION.contentManage}>
        <NewsAdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/pagini',
    element: (
      <RequirePermission permission={PERMISSION.contentManage}>
        <PagesAdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/acasa',
    element: (
      <RequirePermission permission={PERMISSION.contentManage}>
        <SiteHomeAdminPage linkPaths={publicLinkPaths(publicRoutes)} />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/biblioteca',
    element: (
      <RequirePermission permission={PERMISSION.contentManage}>
        <LibraryAdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/harta',
    element: (
      <RequirePermission permission={PERMISSION.geoAdmin}>
        <GeoPointAdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/contact',
    element: (
      <RequirePermission permission={PERMISSION.contentManage}>
        <ContactAdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/backup',
    element: (
      <RequirePermission permission={PERMISSION.backupManage}>
        <BackupAdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin',
    element: (
      <RequirePermission permission={PERMISSION.reportsRead}>
        <AdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/rapoarte/servicii-consiliere',
    element: (
      <RequirePermission permission={PERMISSION.reportsRead}>
        <AdvisoryServicesReportPage />
      </RequirePermission>
    ),
  },
  {
    // tor.md §6.11's evaluations across events, under the analysis endpoint's own permission.
    path: '/app/admin/evaluari-evenimente',
    element: (
      <RequirePermission permission={PERMISSION.reportsRead}>
        <EventEvaluationAnalysisPage scope={EVALUATION_SCOPE.admin} />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/finantare',
    element: (
      <RequirePermission permission={PERMISSION.fundingManage}>
        <FundingAdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/agentii-sprijin',
    element: (
      <RequirePermission permission={PERMISSION.fundingManage}>
        <SupportAgencyAdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/workflows',
    element: (
      <RequirePermission permission={PERMISSION.workflowAdmin}>
        <WorkflowDesignerPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/aprobare',
    element: (
      <RequirePermission permission={PERMISSION.contentApprove}>
        <WorkQueuePage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/consilieri',
    element: (
      <RequirePermission permission={PERMISSION.advisorsValidate}>
        <AdvisorValidationPage queue={WorkQueuePage} />
      </RequirePermission>
    ),
  },
  {
    path: '/app/admin/forumuri',
    element: (
      <RequirePermission permission={PERMISSION.forumsModerate}>
        <ForumAdminPage />
      </RequirePermission>
    ),
  },
];
